import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { intlLocaleFor } from "../../../../i18n/languages";

export const GROUPS = ["materials", "vehicles", "premises", "it", "insurance", "other"];
export function useEconomyText() {
  const { t, i18n } = useTranslation();
  const locale = intlLocaleFor(i18n.language);
  return {
    t: (key, options) => t(`economy.${key}`, options),
    money: (value, digits = 0) => value == null ? "—" : new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(value),
    monthName: (month) => new Intl.DateTimeFormat(locale, { month: "short" }).format(new Date(2024, month - 1, 1)),
  };
}
export function useRemote(load, dependencies) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [revision, refresh] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ loading: true, data: null, error: null });
    load(controller.signal).then((response) => {
      if (active) setState({ loading: false, data: response.data, meta: response, error: null });
    }).catch((error) => {
      if (active && error.kind !== "cancelled") setState({ loading: false, data: null, error });
    });
    return () => { active = false; controller.abort(); };
    // Callers provide the full request identity; load is recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, revision]);
  return { ...state, refresh: () => refresh((value) => value + 1) };
}
export function ErrorBox({ error, onRetry }) {
  const { t } = useEconomyText();
  if (!error) return null;
  return <div className="eco-error" role="alert">
    <p>{error.httpStatus === 409 ? t("conflict") : error.outcome === "unknown" ? t(onRetry ? "loadFailed" : "unknown") : error.safeMessage}</p>
    {!!error.fieldErrors?.length && <ul>{error.fieldErrors.map((field, index) => <li key={index}>{fieldLabel(field.path, t)}: {field.message || t("invalid")}</li>)}</ul>}
    {onRetry && <Button onClick={onRetry}>{t("retry")}</Button>}
  </div>;
}
function fieldLabel(path, t) {
  const parts = String(path || "").split(".");
  const last = parts[parts.length - 1];
  const translated = t(last);
  const label = translated.startsWith("economy.") ? t("settings") : translated;
  const row = parts.find((part) => /^\d+$/.test(part));
  return row == null ? label : `${label} (${t("row")} ${Number(row) + 1})`;
}
export function Field({ label, value, onChange, type = "number", children, ...props }) {
  return <label className="eco-field"><span>{label}</span>{children || <input aria-label={label} type={type} value={value ?? ""} step={type === "number" ? "any" : undefined} onChange={(event) => onChange(event.target.value)} {...props} />}</label>;
}
export function Select({ label, value, onChange, options, ...props }) {
  return <Field label={label}><select aria-label={label} value={value ?? ""} onChange={(event) => onChange(event.target.value)} {...props}>{options.map(([id, title]) => <option key={id} value={id}>{title}</option>)}</select></Field>;
}
export function Button({ className = "", type = "button", ...props }) {
  const variant = className.includes("eco-primary") ? "primary" : "secondary";
  return <button type={type} className={`pm-btn pm-btn--${variant} ${className}`} {...props} />;
}
export function Segmented({ label, value, options, onChange, disabled = false }) {
  return <div className="pm-segmented eco-segmented" role="group" aria-label={label}>
    {options.map(([key, title]) => <button type="button" key={key} className={`pm-segmented__option${String(value) === String(key) ? " is-active" : ""}`} aria-pressed={String(value) === String(key)} disabled={disabled} onClick={() => onChange(key)}>{title}</button>)}
  </div>;
}
export function SectionHeading({ title, description, controls, children }) {
  return <div className="eco-section-heading"><div className="eco-section-copy"><h2>{title}</h2>{description && <p>{description}</p>}</div><div className="eco-actions">{controls}{children}</div></div>;
}
export function Kpis({ values, highlight, hints = {} }) {
  const { t, money } = useEconomyText();
  return <div className="eco-kpis">{Object.entries(values).map(([key, value]) => <div className={`eco-card eco-kpi${highlight === key ? " eco-kpi-highlight" : ""}`} key={key}><span className="eco-label">{t(key)}</span><strong>{money(value, ["fte", "cost_per_hour", "cost_per_km"].includes(key) ? 2 : 0)}</strong>{hints[key] && <span className="eco-kpi-hint">{hints[key]}</span>}</div>)}</div>;
}
export function Guide({ children }) {
  const { t } = useEconomyText();
  return <details className="eco-guide"><summary>{t("methodology")}</summary><p>{children}</p></details>;
}
export function Bars({ rows, valueKey = "value", labelKey = "label" }) {
  const { money } = useEconomyText();
  const max = Math.max(1, ...rows.map((row) => Math.abs(row[valueKey] || 0)));
  return <div className="eco-bars">{rows.map((row, index) => <div className="eco-bar-column" key={index} title={`${row[labelKey]}: ${money(row[valueKey])}`}><span>{money(row[valueKey])}</span><div className="eco-bar-track"><div className={row[valueKey] < 0 ? "negative" : ""} style={{ height: `${Math.abs(row[valueKey] || 0) / max * 100}%` }} /></div><span>{row[labelKey]}</span></div>)}</div>;
}
